// Google Identity Platform auth actions.
//
// Each returns a Firebase ID token, which the app then exchanges with the
// backend (POST /v1/auth/gcp/session) for an HttpOnly session cookie — so the
// SPA never holds the token and get_current_user's GCP path verifies it.
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  multiFactor,
  onIdTokenChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase';
import type {
  MultiFactorError,
  MultiFactorResolver,
  TotpSecret,
  Unsubscribe,
} from 'firebase/auth';

const googleProvider = new GoogleAuthProvider();

// Microsoft is an OAuthProvider rather than a dedicated class — Identity
// Platform models every non-Google IdP that way. `microsoft.com` is the
// provider id it expects, and it must be enabled on the project or the popup
// returns auth/operation-not-allowed. That is why the button is gated on
// config rather than shipped visible: see MICROSOFT_SSO in config.js.
const microsoftProvider = new OAuthProvider('microsoft.com');

/**
 * Run a sign-in and turn "a second factor is required" into `MfaRequiredError`.
 *
 * Every way of signing in can be held for a second factor, not only the
 * password form: Google and Microsoft, by popup or by ID token, throw the same
 * `auth/multi-factor-auth-required` for an enrolled account. Only the password
 * path used to translate it, so an enrolled person signing in with Google got
 * an error instead of the second-factor screen. Funnelling all of them through
 * here means a caller branches on ONE type whichever method was used.
 */
async function holdForSecondFactor<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    if (isMultiFactorError(err)) {
      throw new MfaRequiredError(
        getMultiFactorResolver(getFirebaseAuth(), err),
      );
    }
    throw err;
  }
}

export async function signInWithGoogle(): Promise<string> {
  return holdForSecondFactor(async () => {
    const cred = await signInWithPopup(getFirebaseAuth(), googleProvider);
    return cred.user.getIdToken();
  });
}

/**
 * Sign in with a Google ID token we already hold, without a popup.
 *
 * Used right after the backend has created the account for a brand-new Google
 * address (`/auth/google-signup`). `signInWithPopup` is the wrong tool there
 * for two reasons: a popup opened after awaited network calls is not in a user
 * gesture and gets blocked, and the user has already chosen their Google
 * account once. The same ID token Google Identity Services handed us is a valid
 * credential for Identity Platform's Google provider, so exchange it directly.
 *
 * `rawNonce` is the nonce the token was issued with. It signs in through the
 * ordinary client path, so any enrolled second factor still interrupts it. That
 * surfaces as `MfaRequiredError`, the same as every other sign-in here, and the
 * caller continues into the second-factor screen.
 */
export async function signInWithGoogleIdToken(
  idToken: string,
  rawNonce: string,
): Promise<string> {
  const credential = new OAuthProvider('google.com').credential({
    idToken,
    rawNonce,
  });
  return holdForSecondFactor(async () => {
    const cred = await signInWithCredential(getFirebaseAuth(), credential);
    return cred.user.getIdToken();
  });
}

/**
 * Sign in with a Microsoft ID token we already hold, without a popup.
 *
 * The Microsoft twin of `signInWithGoogleIdToken`, used right after the backend
 * created the account behind `/auth/microsoft-signup`. `rawNonce` is the RAW
 * nonce the server issued: Firebase hashes it and compares with the token's
 * `nonce` claim, which is why the sign-in URL carried the hash.
 */
export async function signInWithMicrosoftIdToken(
  idToken: string,
  rawNonce: string,
): Promise<string> {
  const credential = new OAuthProvider('microsoft.com').credential({
    idToken,
    rawNonce,
  });
  return holdForSecondFactor(async () => {
    const cred = await signInWithCredential(getFirebaseAuth(), credential);
    return cred.user.getIdToken();
  });
}

export async function signInWithMicrosoft(): Promise<string> {
  return holdForSecondFactor(async () => {
    const cred = await signInWithPopup(getFirebaseAuth(), microsoftProvider);
    return cred.user.getIdToken();
  });
}

/**
 * Whether `candidate` is ALREADY this account's password.
 *
 * Used by the reset screen to refuse setting the same password again. A reset
 * is often requested because the old password is suspected of being known by
 * somebody else, and re-entering it produces a flow that looks like it worked
 * and changed nothing. Someone who has genuinely forgotten theirs will rarely
 * trip this; someone guessing at what they had will.
 *
 * HOW, because there is no API that answers this directly. Firebase cannot be
 * asked "is this the password"; the Admin SDK can SET one by uid but cannot
 * verify one, and the reset code can only be redeemed once, so the check has to
 * happen before the reset and cannot use it. So we try the only thing that does
 * answer: a sign-in. If it is accepted, the candidate is the current password.
 *
 * Any session that creates is dropped immediately -- this is a probe, and the
 * user is not signing in. Nothing posts to /auth/gcp/session from this page, so
 * no backend session is minted either.
 *
 * UNCERTAINTY MEANS "NO". A rate limit, a network failure, a disabled account:
 * none of them answer the question, and refusing a legitimate password reset
 * because an ADVISORY check could not run is the worse failure of the two. The
 * reset itself is still gated by the policy rules and the emailed code.
 */
export async function isCurrentPassword(
  email: string,
  candidate: string,
): Promise<boolean> {
  const auth = getFirebaseAuth();
  try {
    await signInWithEmailAndPassword(auth, email, candidate);
    await signOut(auth);
    return true;
  } catch (err) {
    const code =
      typeof err === 'object' && err !== null && 'code' in err
        ? String(err.code)
        : '';
    // Firebase ACCEPTED the password and is holding for a second factor, which
    // answers the question exactly as well as a success does.
    if (code === 'auth/multi-factor-auth-required') return true;
    // `invalid-credential` is what a wrong password returns when the project
    // has email-enumeration protection on; `wrong-password` when it does not.
    if (
      code === 'auth/wrong-password' ||
      code === 'auth/invalid-credential' ||
      code === 'auth/user-not-found'
    ) {
      return false;
    }
    return false;
  }
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<string> {
  // The password may be right while Identity Platform holds the session until
  // a second factor is supplied. `holdForSecondFactor` re-throws something the
  // UI can branch on WITHOUT leaking the resolver into a generic error message.
  return holdForSecondFactor(async () => {
    const cred = await signInWithEmailAndPassword(
      getFirebaseAuth(),
      email,
      password,
    );
    return cred.user.getIdToken();
  });
}

export interface GcpSignUpResult {
  idToken: string;
  /** Firebase uid — the backend's /auth/resend-verification takes this as user_id. */
  uid: string;
}

export async function signUpWithPassword(
  email: string,
  password: string,
): Promise<GcpSignUpResult> {
  const cred = await createUserWithEmailAndPassword(
    getFirebaseAuth(),
    email,
    password,
  );
  // NOTE: deliberately does NOT call sendEmailVerification here.
  //
  // It used to, as `void sendEmailVerification(cred.user).catch(() => undefined)`
  // — fire-and-forget with the error DISCARDED. That sent Firebase's own
  // unbranded template (never configured for this project) and, when it failed,
  // failed silently: no log, no toast, no trace. That is why "verification
  // emails stopped working after the Firebase migration" was so hard to see.
  //
  // The caller now asks the BACKEND to send it (/auth/resend-verification,
  // which has a gcp branch since #1073): the backend mints a fresh Firebase
  // action link via the Admin SDK and delivers it through our own branded SMTP
  // transport, where the send is logged and failures surface.
  return { idToken: await cred.user.getIdToken(), uid: cred.user.uid };
}

/** The signed-in Firebase user's uid, for backend calls that key on it. */
export function currentUid(): string | null {
  return getFirebaseAuth().currentUser?.uid ?? null;
}

/** Whether the signed-in Firebase user has verified their email. */
export function isEmailVerified(): boolean {
  return getFirebaseAuth().currentUser?.emailVerified ?? false;
}

/** Fresh ID token for the signed-in user (or null). Used to re-exchange the
 *  session cookie when Firebase rotates the token. */
export async function getCurrentIdToken(
  forceRefresh = false,
): Promise<string | null> {
  const auth = getFirebaseAuth();
  await auth.authStateReady();
  const user = auth.currentUser;
  return user ? user.getIdToken(forceRefresh) : null;
}

/**
 * Keep the backend session cookie fresh across the Firebase token lifecycle.
 *
 * Firebase ID tokens expire after ~1 hour; the SDK auto-rotates them a few
 * minutes before expiry and fires `onIdTokenChanged` on every rotation (and on
 * sign-in / sign-out). Without re-posting the rotated token the HttpOnly
 * session cookie goes stale mid-session and the next API call 401s. The
 * callback receives the fresh token (or null on sign-out). Returns the
 * Firebase unsubscribe fn.
 */
export function subscribeIdTokenRefresh(
  onToken: (idToken: string | null) => void,
): Unsubscribe {
  return onIdTokenChanged(getFirebaseAuth(), async (user) => {
    onToken(user ? await user.getIdToken() : null);
  });
}

/**
 * Send a Firebase password-reset email.
 *
 * NOT USED IN PRODUCTION, and kept only so a test can assert it is not called.
 *
 * This sends GOOGLE's email, in Google's words, pointing at Google's hosted
 * page. /auth/forgot-password does the thing we want instead — our template,
 * and a link rebased onto our own /auth/action. ForgotPasswordPage called this
 * one for a long time, so none of that ran; the test that caught it asserts
 * `sendPasswordReset` was NOT reached, which is what keeps it from coming back.
 * That assertion needs the export to exist.
 *
 * Swallows `auth/user-not-found` so we never reveal whether an email is
 * registered (account-enumeration protection), matching our own endpoint's
 * "always succeed" behaviour.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(getFirebaseAuth(), email);
  } catch (err) {
    const code =
      typeof err === 'object' && err !== null && 'code' in err
        ? String(err.code)
        : '';
    if (code === 'auth/user-not-found' || code === 'auth/invalid-email') return;
    throw err;
  }
}

/**
 * Change the signed-in user's password against Identity Platform. Firebase
 * requires a recent login, so we reauthenticate with the current password
 * first (this also verifies it) before updating — the GCP equivalent of the
 * old authenticate-then-update flow.
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  if (!oldPassword) throw new Error('Current password is required');
  const user = getFirebaseAuth().currentUser;
  if (!user || !user.email) throw new Error('Not signed in');
  await reauthenticateWithCredential(
    user,
    EmailAuthProvider.credential(user.email, oldPassword),
  );
  await updatePassword(user, newPassword);
}

export async function signOutGcp(): Promise<void> {
  await signOut(getFirebaseAuth());
}

// ---------------------------------------------------------------------------
// Email action codes (the `oobCode` in the links Firebase mails out)
//
// These back the /auth/action route, which is registered in the Firebase
// console as the project's custom "action URL". Without it, every verification
// and password-reset link lands on Google's own grey, unbranded handler page.
// ---------------------------------------------------------------------------

/**
 * Redeem a one-time action code. Used for `verifyEmail` (marks the account
 * verified) and `recoverEmail` (reverts an unwanted address change).
 *
 * Throws Firebase's `auth/invalid-action-code` when the link was already used
 * or has expired — callers must distinguish that from a real failure, because
 * "already used" usually means the user simply clicked the link twice.
 */
export async function applyEmailActionCode(oobCode: string): Promise<void> {
  await applyActionCode(getFirebaseAuth(), oobCode);
}

/**
 * Validate a password-reset code and return the address it belongs to, so the
 * reset screen can show WHOSE password is being changed before accepting one.
 */
export async function verifyPasswordReset(oobCode: string): Promise<string> {
  return verifyPasswordResetCode(getFirebaseAuth(), oobCode);
}

/** Complete a password reset with a validated code. */
export async function confirmPasswordResetWithCode(
  oobCode: string,
  newPassword: string,
): Promise<void> {
  await confirmPasswordReset(getFirebaseAuth(), oobCode, newPassword);
}

/**
 * Re-read the signed-in user from Identity Platform.
 *
 * Firebase caches `emailVerified` on the local user object; after the account
 * is verified in another tab (or by the /auth/action route) the cached copy is
 * still stale until reloaded, which is what made "I've confirmed my email"
 * appear to do nothing.
 */
export async function reloadCurrentUser(): Promise<boolean> {
  const user = getFirebaseAuth().currentUser;
  if (!user) return false;
  await user.reload();
  return user.emailVerified;
}

// ---------------------------------------------------------------------------
// Multi-factor authentication (TOTP / authenticator app)
//
// Identity Platform owns the secret and the verification; we never see or
// store either. Enrolment binds a factor to the Firebase user, and from then
// on password sign-in returns `auth/multi-factor-auth-required` instead of a
// session until a valid code is supplied.
// ---------------------------------------------------------------------------

/** One enrolled second factor, as shown in the security settings list. */
export interface MfaFactor {
  /** Firebase's id for this factor — what `unenrollFactor` takes. */
  uid: string;
  /** Firebase factor id — 'phone' is the only one we enrol. */
  factorId: string;
  /** What the user named it at enrolment ("iPhone", "1Password"). */
  displayName: string | null;
  enrolledAt: string | null;
  /** Obfuscated number, on a phone hint only. Identity Platform masks it. */
  phoneNumber?: string | null;
}

/**
 * Thrown by `signInWithPassword` when the password was correct but a second
 * factor is required.
 *
 * Carries Firebase's resolver, which holds the half-finished sign-in. The
 * resolver is single-use and short-lived: the caller must keep THIS object and
 * pass it back to `resolvePhoneSignIn`, because re-running the password step
 * would produce a different one.
 */
export class MfaRequiredError extends Error {
  /** Firebase's own code, so code-based checks treat this like the original. */
  readonly code = 'auth/multi-factor-auth-required';
  readonly resolver: MultiFactorResolver;
  readonly hints: Array<MfaFactor>;

  constructor(resolver: MultiFactorResolver) {
    super('Multi-factor authentication required');
    this.name = 'MfaRequiredError';
    this.resolver = resolver;
    this.hints = resolver.hints.map((hint) => ({
      uid: hint.uid,
      factorId: hint.factorId,
      displayName:
        typeof hint.displayName === 'string' ? hint.displayName : null,
      enrolledAt:
        typeof hint.enrollmentTime === 'string' ? hint.enrollmentTime : null,
      // Carried through so the challenge can say which handset the code went
      // to. It was dropped here, so `pickMfaFactor` always saw no number and
      // the screen could never name one.
      phoneNumber:
        'phoneNumber' in hint && typeof hint.phoneNumber === 'string'
          ? hint.phoneNumber
          : null,
    }));
  }
}

function isMultiFactorError(err: unknown): err is MultiFactorError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    (err as { code?: string }).code === 'auth/multi-factor-auth-required'
  );
}

/** Second factors currently enrolled on the signed-in account. */
export function enrolledMfaFactors(): Array<MfaFactor> {
  const user = getFirebaseAuth().currentUser;
  if (!user) return [];
  return multiFactor(user).enrolledFactors.map((factor) => ({
    uid: factor.uid,
    factorId: factor.factorId,
    displayName:
      typeof factor.displayName === 'string' ? factor.displayName : null,
    enrolledAt:
      typeof factor.enrollmentTime === 'string' ? factor.enrollmentTime : null,
  }));
}

/** Remove an enrolled factor (TOTP or phone). Also requires a recent login. */
export async function unenrollFactor(factorUid: string): Promise<void> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  await multiFactor(user).unenroll(factorUid);
}

// ---------------------------------------------------------------------------
// Authenticator app (TOTP)
//
// Restored from 550f80a0f, which built this end to end before #1129 dropped it
// "on your call" — no reason recorded beyond the product decision and the
// `qrcode` dependency it was the sole cause of. Reinstated with that dependency
// back, deliberately.
//
// TOTP is RFC 6238 and Firebase hands back the standard Key Uri Format
// (`otpauth://`) plus the raw base32 seed, so ANY authenticator reads it —
// Google, Microsoft, 1Password, Authy, Bitwarden, Apple Passwords. There is no
// brand to adopt and no vendor to depend on.
//
// It is also the only factor here with no delivery channel: nothing to send,
// nothing to be provisioned, nothing to be deferred by a receiving server. That
// is why it works on a laptop with no SMTP and no SMS provider, which is the
// state this repo's local stack is in.
// ---------------------------------------------------------------------------

export interface TotpEnrollmentStart {
  /** Opaque Firebase handle — pass back to `completeTotpEnrollment`. */
  secret: TotpSecret;
  /** `otpauth://` URI to render as a QR code. */
  qrCodeUrl: string;
  /** Base32 key, for users who cannot scan and type it in instead. */
  manualKey: string;
}

export async function startTotpEnrollment(
  accountLabel: string,
): Promise<TotpEnrollmentStart> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    secret,
    // The issuer is what shows up as the account name in the authenticator
    // app, so it has to be recognisable next to the user's other codes.
    qrCodeUrl: secret.generateQrCodeUrl(accountLabel, 'BIASafe'),
    manualKey: secret.secretKey,
  };
}

export async function completeTotpEnrollment(
  secret: TotpSecret,
  oneTimeCode: string,
  displayName: string,
): Promise<void> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    secret,
    oneTimeCode.trim(),
  );
  await multiFactor(user).enroll(
    assertion,
    displayName.trim() || 'Authenticator app',
  );
  // Re-read the user, for the reason `completePhoneEnrollment` does: Firebase
  // caches the factor list on the local user, so `enrolledMfaFactors()` right
  // after enrolling returned the list from BEFORE it.
  await user.reload();
}

/** Finish a sign-in that Identity Platform held back for a TOTP code. */
export async function resolveTotpSignIn(
  resolver: MultiFactorResolver,
  factorUid: string,
  oneTimeCode: string,
): Promise<string> {
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    factorUid,
    oneTimeCode.trim(),
  );
  const cred = await resolver.resolveSignIn(assertion);
  return cred.user.getIdToken();
}

// ---------------------------------------------------------------------------
// SMS second factor
//
// Identity Platform sends these itself — this is NOT our Twilio transport, so
// it works regardless of whether SMS is provisioned on our side.
//
// Every phone flow needs a reCAPTCHA verifier: Google uses it to stop an
// attacker burning someone's SMS quota (or our billing) by hammering the
// send endpoint. `invisible` runs without user interaction in the normal case
// and only shows a challenge when Google is suspicious.
// ---------------------------------------------------------------------------

let _verifier: RecaptchaVerifier | null = null;

/**
 * The shared invisible reCAPTCHA verifier, created once per page.
 *
 * Firebase ties a verifier to a DOM element and refuses to reuse one that has
 * already solved a challenge, so a stale instance produces
 * `auth/internal-error` on the SECOND send — which is exactly the kind of
 * thing that only shows up once a real user retries. `clearPhoneVerifier`
 * disposes it after every flow so the next one starts clean.
 */
function phoneVerifier(containerId: string): RecaptchaVerifier {
  if (_verifier) return _verifier;
  _verifier = new RecaptchaVerifier(getFirebaseAuth(), containerId, {
    size: 'invisible',
  });
  return _verifier;
}

/** Dispose the verifier. Safe to call when none exists. */
export function clearPhoneVerifier(): void {
  try {
    _verifier?.clear();
  } catch {
    // Already torn down with its container — nothing to release.
  }
  _verifier = null;
}

/**
 * Send an enrolment SMS. Returns the verification id to pass back with the
 * code the user receives.
 *
 * `phoneNumber` must be E.164 (+15551234567); Identity Platform rejects
 * anything else with `auth/invalid-phone-number`.
 */
export async function startPhoneEnrollment(
  phoneNumber: string,
  recaptchaContainerId: string,
): Promise<string> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const session = await multiFactor(user).getSession();
  const provider = new PhoneAuthProvider(getFirebaseAuth());
  // Dispose before creating, not only after the flow ends. A verifier that has
  // solved its challenge cannot solve another, and the send that reuses one
  // fails with `auth/internal-error` — which the enrolment screen showed as
  // "that code did not match", against a code the user had not typed yet.
  // Every retry path led here: a mistyped number, a wrong country code, a
  // resend after a slow SMS. One verifier per send removes the class.
  clearPhoneVerifier();
  return provider.verifyPhoneNumber(
    { phoneNumber, session },
    phoneVerifier(recaptchaContainerId),
  );
}

/** Confirm phone enrolment with the code from the SMS. */
export async function completePhoneEnrollment(
  verificationId: string,
  smsCode: string,
  displayName: string,
): Promise<void> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const cred = PhoneAuthProvider.credential(verificationId, smsCode.trim());
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  await multiFactor(user).enroll(assertion, displayName.trim() || 'Phone');
  // Then re-read the user, for the same reason `reloadCurrentUser` exists.
  //
  // Firebase caches the multi-factor list on the local user object exactly as
  // it caches `emailVerified`, so `enrolledMfaFactors()` called straight after
  // this returned the list from BEFORE the enrolment — empty, on the first
  // factor. The mandatory setup screen reads that list to decide whether
  // anything is enrolled, so a successful enrolment rendered as "no methods
  // set up": the user typed a code that worked, got Identity Platform's own
  // "a second factor was added" email, and was shown the choose-a-method step
  // again with no error to explain it.
  //
  // This is the `emailVerified` bug one field over — that one made "I've
  // confirmed my email" appear to do nothing, and was fixed the same way.
  // Reloading here rather than in the caller keeps every caller correct: the
  // staleness is a Firebase detail, and this module is where those live.
  await user.reload();
}

/**
 * Send the sign-in SMS for a challenge already in progress.
 *
 * Uses the resolver's session rather than a fresh one: the user is NOT signed
 * in at this point, so there is no `currentUser` to take a session from.
 */
export async function startPhoneSignIn(
  resolver: MultiFactorResolver,
  factorUid: string,
  recaptchaContainerId: string,
): Promise<string> {
  const hint = resolver.hints.find((h) => h.uid === factorUid);
  if (!hint) throw new Error('Unknown second factor');
  const provider = new PhoneAuthProvider(getFirebaseAuth());
  // One verifier per send, as in `startPhoneEnrollment`: a verifier that has
  // already solved its challenge fails the NEXT send with auth/internal-error,
  // which is what a "Resend code" at sign-in hit.
  clearPhoneVerifier();
  return provider.verifyPhoneNumber(
    { multiFactorHint: hint, session: resolver.session },
    phoneVerifier(recaptchaContainerId),
  );
}

/** Finish a phone challenge. */
export async function resolvePhoneSignIn(
  resolver: MultiFactorResolver,
  verificationId: string,
  smsCode: string,
): Promise<string> {
  const cred = PhoneAuthProvider.credential(verificationId, smsCode.trim());
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  const result = await resolver.resolveSignIn(assertion);
  return result.user.getIdToken();
}
